import { createClient, FunctionsHttpError } from '@supabase/supabase-js';
import { env, devLog } from './env';

import { logger } from './logger';
import { recordSessionPresence, seedSessionPresence } from './session-presence';
import { withPostgrestStatusBodies } from './postgrest-fetch';
// Environment variables are validated in env.ts

// Log which environment we're connecting to.  Driven by VITE_ENVIRONMENT
// rather than substring-matching the Supabase URL so we don't bake project
// IDs into the client bundle.  The URL itself is intentionally omitted to
// avoid leaking the project host in browser console logs.
const environmentLabel = (env.VITE_ENVIRONMENT ?? 'unknown').toUpperCase();
logger.info(`🔗 Connecting to ${environmentLabel} Supabase environment`);

// Initialize with better error handling and auto-refresh
export const supabase = createClient(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  // Body-less REST errors (every HEAD count query) get a body carrying the HTTP
  // status, so a 401 is recognisable as a dead session — see postgrest-fetch.ts.
  // `fetch` is looked up per call: unbound it throws "Illegal invocation" in Safari.
  global: { fetch: withPostgrestStatusBodies((input, init) => fetch(input, init)) },
});

/**
 * Invoke a Supabase Edge Function with an automatic session refresh beforehand.
 *
 * Safari (especially on macOS Catalina) throttles background tabs, which can
 * cause the Supabase auto-refresh to stall and leave the JWT expired.  By
 * refreshing before Edge Function calls we avoid 401s that surface as
 * FunctionsHttpError in Sentry.
 *
 * Drop-in replacement for `supabase.functions.invoke()`.
 */

/** Headroom below which we proactively refresh the access token (1 minute). */
const REFRESH_HEADROOM_MS = 60_000;

/**
 * Single in-flight refresh shared by all concurrent callers.
 *
 * Why this matters: supabase-js serialises token rotation using the Web Locks
 * API (`navigator.locks`). Older browsers WITHOUT Web Locks — notably Safari on
 * macOS Catalina (Safari < 15.4) — have no such lock, so multiple
 * `refreshSession()` calls firing at once (the dashboard mounts ~10 hooks, several
 * of which invoke edge functions) rotate the refresh token in parallel. One then
 * presents an already-rotated token and the server returns "Invalid Refresh
 * Token: Already Used" → an AuthApiError that the global `unhandledrejection`
 * handler in main.tsx turns into a forced sign-out. That logged a Catalina-iMac
 * user out a few seconds after every login. Collapsing concurrent refreshes to
 * one in-flight call removes the race on every browser.
 */
let inFlightRefresh: Promise<{ ok: boolean; error?: unknown }> | null = null;

/** True when the cached access token still has comfortable headroom. */
async function accessTokenIsFresh(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.expires_at) return false;
    return session.expires_at * 1000 - Date.now() > REFRESH_HEADROOM_MS;
  } catch {
    return false;
  }
}

export async function tryRefreshSession(): Promise<{ ok: boolean; error?: unknown }> {
  // Don't rotate a token that is still valid. Right after login the token is
  // brand new, so this skips the refresh storm that the dashboard's concurrent
  // edge-function calls would otherwise trigger. The Safari background-throttle
  // workaround still fires once the token actually nears expiry.
  if (await accessTokenIsFresh()) return { ok: true };

  // Dedupe: concurrent callers share one refresh so they can't rotation-race.
  if (!inFlightRefresh) {
    inFlightRefresh = (async () => {
      try {
        const { error } = await supabase.auth.refreshSession();
        return error ? { ok: false, error } : { ok: true };
      } catch (err) {
        return { ok: false, error: err };
      }
    })().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

/**
 * The common body of an edge function's 200 reply. Older builds answered a
 * refusal with 200 + `{ success: false, error }`, so callers still check both.
 */
export interface EdgeFunctionReply {
  success?: boolean;
  error?: string;
}

export async function invokeEdgeFunction<T = unknown>(
  functionName: string,
  options?: { body?: Record<string, unknown> },
) {
  let refresh = await tryRefreshSession();

  if (!refresh.ok) {
    logger.warn(
      'Supabase session refresh failed; retrying once before invoking edge function',
      { functionName, error: String(refresh.error) },
      'invokeEdgeFunction',
    );
    await new Promise((resolve) => setTimeout(resolve, 250));
    refresh = await tryRefreshSession();
  }

  if (!refresh.ok) {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      // No valid cached session either – surface a 401 to the caller instead
      // of letting the edge function fail opaquely with a stale or missing JWT.
      logger.warn(
        'Aborting edge function invocation: no valid session after refresh failure',
        { functionName, error: String(refresh.error) },
        'invokeEdgeFunction',
      );
      const response = new Response(
        JSON.stringify({ message: 'Authentication required: session refresh failed' }),
        { status: 401, statusText: 'Unauthorized', headers: { 'Content-Type': 'application/json' } },
      );
      return { data: null as T | null, error: new FunctionsHttpError(response) };
    }
    // Cached session still exists – proceed with best-effort invocation, but
    // the failure has now been logged instead of silently swallowed.
  }

  return supabase.functions.invoke<T>(functionName, options);
}

// Add error handling for initialization
supabase.auth.onAuthStateChange((event, session) => {
  // Mirror the session into the synchronous flag `handleSupabaseError` reads.
  // Set from the session on EVERY event rather than from the event name, so a
  // new or renamed auth event can't silently stop tracking.
  recordSessionPresence(!!session);

  if (event === 'SIGNED_OUT') {
    devLog('User signed out');
  } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
    devLog('Auth state changed:', event, 'User ID:', session?.user?.id);
  }
});

// Seed the mirror for SDK versions that don't emit an initial event. Seeding
// never overwrites a real auth event, so this losing the race is harmless.
// Fully guarded because this runs at module scope: a partially-mocked client in
// a test, or an SDK that changes this signature, must not take the module's
// import down with it. Failing here just leaves the state unknown, and unknown
// reports — the safe direction.
void (async () => {
  try {
    const { data } = await supabase.auth.getSession();
    seedSessionPresence(!!data?.session);
  } catch {
    /* leave unknown */
  }
})();